import { spawn, type ChildProcess, type SpawnOptions } from 'node:child_process';

/** spawn 相容型別：測試可注入假實作，不必真的產生行程。 */
export type SpawnFn = (command: string, args: readonly string[], options: SpawnOptions) => ChildProcess;

interface BrowserCommand {
	command: string;
	args: string[];
}

/**
 * URL 安全閘（security-checklist：API 回應是外部輸入，不得進 shell）：
 * - 僅開 http(s)——`javascript:`/`file:` 等 scheme 交給瀏覽器以外的 handler 是攻擊面
 * - win32 額外拒開含 cmd metacharacters 的 URL：`cmd /c start` 的參數會被 cmd.exe 重新解析，
 *   spawn 的陣列參數形式擋不住 `&`/`|` 這類串接（`?a=1&b=2` 會在 & 後當新指令執行）。
 *   合法的 device-flow 驗證頁 URL（`https://wport.me/activate?user_code=XXXX-XXXX`）不含
 *   這些字元，拒開零誤傷；被拒時回 false，呼叫端 fallback 印 URL 讓使用者自己貼。
 */
function isSafeToOpen(url: string, platform: NodeJS.Platform): boolean {
	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		return false;
	}
	if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
	if (platform === 'win32' && /[&|^<>%"]/.test(url)) return false;
	return true;
}

/** 依平台決定開瀏覽器要跑的指令；不支援的平台（如 sunos/aix 等罕見值）回 null 讓呼叫端 fallback。 */
function commandFor(platform: NodeJS.Platform, url: string): BrowserCommand | null {
	switch (platform) {
		case 'darwin':
			return { command: 'open', args: [url] };
		case 'win32':
			// cmd /c start 的第一個參數會被當成視窗標題吃掉；空字串佔位避免 URL 被誤判成標題（常見坑）。
			return { command: 'cmd', args: ['/c', 'start', '""', url] };
		case 'linux':
			return { command: 'xdg-open', args: [url] };
		default:
			return null;
	}
}

/**
 * 跨平台開啟瀏覽器。detached + unref：CLI 進程不該被瀏覽器行程拖住——瀏覽器可能長駐執行，
 * 若父子行程綁在一起，CLI 會被迫等到使用者關掉瀏覽器才結束，體感上完全不合理。
 *
 * 失敗處理分兩種情況：
 * - 同步丟例外（不支援的平台 / spawn 呼叫本身炸掉）→ try/catch 接住，回 false，
 *   呼叫端（login 命令）在這之上疊一層 fallback：印出網址讓使用者自己複製貼上。
 * - 非同步 'error' event（例如指令真的不存在的 ENOENT）→ spawn() 是先同步回傳 ChildProcess，
 *   錯誤才在下一輪事件迴圈以 'error' event 送達；此時函式早已同步回傳 true。EventEmitter 對
 *   沒人監聽的 'error' event 會直接重新丟出、讓整個 process crash，所以這裡必須 attach 一個
 *   被動的 listener 吞掉它，換取「CLI 不會被開瀏覽器這種錦上添花的動作拖垮」——代價是這個情境下
 *   回傳值仍是（已經回傳過的）true，無法回頭改成 false。
 */
export function openInBrowser(url: string, spawnFn: SpawnFn = spawn): boolean {
	if (!isSafeToOpen(url, process.platform)) return false;
	const resolved = commandFor(process.platform, url);
	if (!resolved) return false;
	try {
		const child = spawnFn(resolved.command, resolved.args, { detached: true, stdio: 'ignore' });
		child.on('error', () => {
			// 故意不做事：目的只是避免 unhandled 'error' event 讓 process crash，
			// 此時已經來不及回報失敗給呼叫端了。
		});
		child.unref();
		return true;
	} catch {
		return false;
	}
}
